import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import DepartureDialog from './DepartureDialog';
import BanDialog from './BanDialog';
import EvictionForm from './EvictionForm';
import PrintableForm from './PrintableForm';
import {
  Users,
  Plus,
  Printer,
  Search,
  Calendar,
  Phone,
  MapPin,
  UserPlus,
  FileText,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Edit,
  Trash2,
  LogOut,
  UserX,
  UserCheck,
  CalendarDays,
  Gavel,
  MessageSquarePlus,
  PenTool,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import ResidentDocumentPrint from './ResidentDocumentPrint';
import ConsentCaptureDialog from './ConsentCaptureDialog';
import ResidentFiles from './ResidentFiles';
import { escapeDeep } from '../lib/escapeHtml';
import ImportSpreadsheet from './ImportSpreadsheet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const ResidentManagement = () => {
  const { user } = useAuth();
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [consentCounts, setConsentCounts] = useState({});
  const [rooms, setRooms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('current');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingResident, setEditingResident] = useState(null);
  const [showDepartDialog, setShowDepartDialog] = useState(false);
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [selectedResidentForAction, setSelectedResidentForAction] = useState(null);
  const [printData, setPrintData] = useState(null);
  const [showResidentPrintDialog, setShowResidentPrintDialog] = useState(false);
  const [printResident, setPrintResident] = useState(null);
  const [printType, setPrintType] = useState('resident_profile');
  
  // Staff management states
  const [addMode, setAddMode] = useState('resident'); // 'resident' or 'staff'
  const [staffFormData, setStaffFormData] = useState({
    full_name: '',
    username: '',
    email: '',
    password: '',
    role: 'staff'
  });
  const [allStaff, setAllStaff] = useState([]);
  const [showDeleteStaffDialog, setShowDeleteStaffDialog] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState(null);
  const [showDeleteResidentDialog, setShowDeleteResidentDialog] = useState(false);
  const [filesResident, setFilesResident] = useState(null);
  const [showFilesDialog, setShowFilesDialog] = useState(false);
  const [residentToDelete, setResidentToDelete] = useState(null);
  const isGeneralStaff = user?.role === 'general_staff';
  const [showChangeRequestDialog, setShowChangeRequestDialog] = useState(false);
  const [changeRequestResident, setChangeRequestResident] = useState(null);
  const [changeRequestText, setChangeRequestText] = useState('');
  const [showConsentCapture, setShowConsentCapture] = useState(false);
  const [consentResident, setConsentResident] = useState(null);
  const [consentEnforce, setConsentEnforce] = useState(false);

  const openChangeRequest = (resident) => {
    setChangeRequestResident(resident);
    setChangeRequestText('');
    setShowChangeRequestDialog(true);
  };

  const submitChangeRequest = async () => {
    if (!changeRequestText.trim()) {
      toast.error('Please describe the change needed');
      return;
    }
    try {
      await axios.post(`${API}/resident-change-requests`, {
        resident_id: changeRequestResident.id,
        resident_name: `${changeRequestResident.first_name} ${changeRequestResident.last_name}`,
        change_description: changeRequestText
      });
      toast.success('Change request submitted for case worker review');
      setShowChangeRequestDialog(false);
      setChangeRequestText('');
      setChangeRequestResident(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit change request');
    }
  };
  
  const [departData, setDepartData] = useState({
    departure_reason: 'departed',
    departure_notes: '',
    forwarding_address: '',
    destination_shelter_type: 'emergency'
  });
  
  const [banData, setBanData] = useState({
    ban_reason: '',
    ban_notes: ''
  });
  
  const [formData, setFormData] = useState({
    // Basic Information
    first_name: '',
    last_name: '',
    date_of_birth: '',
    intake_date: new Date().toISOString().split('T')[0], // Default to today
    social_security_number: '',
    insurance_type: '', // Medicare, Medicaid, private, none, VA, kid care
    primary_language: '',
    
    // Demographics
    gender: '',
    race: '',
    ethnicity: '',
    
    // Status Information
    veteran_status: '',
    disability_status: '',
    marital_status: '',
    household_composition: '',
    family_members: [],
    
    // Contact Information
    phone: '',
    email: '',
    emergency_contact: '',
    emergency_phone: '',
    
    // Homeless Status and Living Situation
    previous_living_situation: '',
    homeless_duration: '',
    times_homeless_past_three_years: '',
    
    // Income and Benefits
    employment_status: '',
    income_sources: [],
    monthly_income: '',
    receives_benefits: '',
    // New income types
    income_food_stamps: false,
    income_tanf: false,
    income_social_security: false,
    income_employment_ft: false,
    income_employment_pt: false,
    income_ssa: false,
    income_ssi: false,
    income_ssdi: false,
    income_other: false,
    income_other_amount: '',
    
    // Health and Disability Information
    medical_notes: '',
    special_needs: '',
    dietary_restrictions: '',
    other_allergies: '',
    mental_health_issues: '',
    substance_abuse_issues: '',
    // New disability issues
    disability_drug_problem: false,
    disability_alcohol_problem: false,
    disability_physical_mental_health: false,
    disability_developmental: false,
    disability_physical_handicap: false,
    disability_hiv_aids: false,
    // New medical issues
    medical_drug_problem: false,
    medical_alcohol_problem: false,
    medical_physical_health: false,
    medical_communicable_disease: false,
    medical_learning_disability: false,
    medical_add_adhd: false,
    medical_diabetes: false,
    medical_seizures: false,
    
    // Housing History
    education_level: '',
    housing_history: '',
    will_relocate: '', // yes, no
    housing_priority_list: '', // yes, no
    
    // Domestic Violence
    domestic_violence_survivor: '',
    
    // Program Enrollment Information
    case_manager: '',
    service_plan_goals: '',
    
    // Additional Information
    additional_notes: '',
    referral_source: '',
    cpd_clear: '',
    drug_screen: '',
    criminal_convictions: '', // Misdemeanor, Felony, Both, Client refused
    // PATH Program (SAMHSA)
    path_enrolled: '',
    path_status: '',
    path_date_of_engagement: '',
    path_date_of_status_determination: '',
    path_connection_soar: '',
    path_current_living_situation: '',
    path_services_provided: '',
    path_referrals_provided: ''
  });

  useEffect(() => {
    fetchResidents();
    fetchConsentCounts();
    fetchRooms();
  }, [selectedStatus]);

  useEffect(() => {
    if (addMode === 'staff' && user) {
      console.log('Fetching staff because addMode changed to staff and user is:', user);
      fetchStaff();
    }
  }, [addMode, user]);

  const REQUIRED_CONSENTS = ['roi', 'house_rules', 'coc'];

  const fetchRooms = async () => {
    try {
      const r = await axios.get(`${API}/rooms`);
      setRooms(r.data || []);
    } catch (e) { console.warn('background fetch failed:', e?.response?.status); }
  };

  // Open beds compatible with a resident's gender (trans MTF->female, FTM->male)
  const GENDER_TO_ROOM = {
    male: 'male', transgender_ftm: 'male', transgender_male: 'male',
    female: 'female', transgender_mtf: 'female', transgender_female: 'female',
  };
  const compatibleRooms = (resident) => {
    const eff = GENDER_TO_ROOM[(resident.gender || '').toLowerCase()];
    return rooms.filter((r) =>
      r.status !== 'maintenance' &&
      (r.capacity - r.current_occupancy) > 0 &&
      (r.gender === 'any' || r.gender === 'family' || !eff || r.gender === eff)
    );
  };

  const assignToRoom = async (roomId, residentId) => {
    try {
      await axios.put(`${API}/rooms/${roomId}/assign/${residentId}`);
      toast.success('Resident assigned to room');
      fetchResidents();
      fetchRooms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign resident');
    }
  };

  const fetchConsentCounts = async () => {
    try {
      const r = await axios.get(`${API}/consent-records`);
      const counts = {};
      (r.data || []).forEach((rec) => {
        if (!REQUIRED_CONSENTS.includes(rec.document_type)) return;
        counts[rec.resident_id] = counts[rec.resident_id] || new Set();
        counts[rec.resident_id].add(rec.document_type);
      });
      const result = {};
      Object.keys(counts).forEach((rid) => { result[rid] = counts[rid].size; });
      setConsentCounts(result);
    } catch (e) { console.warn('background fetch failed:', e?.response?.status); }
  };

  const fetchResidents = async () => {
    try {
      setLoading(true);
      // Add cache-busting parameter to force fresh data
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API}/residents?status=${selectedStatus}&_t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      console.log(`Residents (${selectedStatus}) response:`, response.data?.length || 0, 'residents'); // Debug log
      setResidents(response.data || []);
    } catch (error) {
      console.error('Error fetching residents:', error);
      console.error('Error details:', error.response?.data);
      toast.error(`Failed to load residents: ${error.response?.data?.detail || error.message}`);
      setResidents([]); // Set empty array to prevent undefined errors
    } finally {
      setLoading(false);
    }
  };

  const transformFormData = (data) => {
    const transformed = { ...data };
    
    // Convert numeric fields
    if (transformed.times_homeless_past_three_years) {
      transformed.times_homeless_past_three_years = parseInt(transformed.times_homeless_past_three_years) || null;
    } else {
      transformed.times_homeless_past_three_years = null;
    }
    
    if (transformed.monthly_income) {
      transformed.monthly_income = parseFloat(transformed.monthly_income) || null;
    } else {
      transformed.monthly_income = null;
    }
    
    // Convert boolean fields - convert yes/no strings to booleans
    const booleanFields = ['mental_health_issues', 'substance_abuse_issues', 'receives_benefits'];
    booleanFields.forEach(field => {
      if (transformed[field] === 'yes' || transformed[field] === true) {
        transformed[field] = true;
      } else if (transformed[field] === 'no' || transformed[field] === false) {
        transformed[field] = false;
      } else {
        transformed[field] = null;
      }
    });
    
    // Handle empty strings as null for optional fields
    Object.keys(transformed).forEach(key => {
      if (transformed[key] === '') {
        transformed[key] = null;
      }
    });
    
    // Ensure income_sources is an array (it should already be from the form)
    if (!Array.isArray(transformed.income_sources)) {
      transformed.income_sources = [];
    }

    // Only send family members that have at least a first & last name
    if (Array.isArray(transformed.family_members)) {
      const cleaned = transformed.family_members
        .filter((m) => m && m.first_name?.trim() && m.last_name?.trim())
        .map((m) => ({
          first_name: m.first_name.trim(),
          last_name: m.last_name.trim(),
          date_of_birth: m.date_of_birth || null,
          gender: m.gender || null,
          social_security_number: m.social_security_number || null,
          relationship_to_head: m.relationship_to_head || 'child',
        }));
      transformed.family_members = cleaned.length ? cleaned : null;
    }
    
    return transformed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (addMode === 'staff') {
      await handleStaffSubmit();
      return;
    }

    if (!formData.gender) {
      toast.error('Gender is required');
      return;
    }
    
    try {
      const transformedData = transformFormData(formData);
      
      if (editingResident) {
        delete transformedData.family_members;
        await axios.put(`${API}/residents/${editingResident.id}`, transformedData);
        toast.success('Resident updated successfully');
        resetForm();
        fetchResidents();
      } else {
        const response = await axios.post(`${API}/residents`, transformedData);
        const famCount = Array.isArray(transformedData.family_members) ? transformedData.family_members.length : 0;
        toast.success(famCount > 0
          ? `Household added (${famCount + 1} people) — please capture required signatures`
          : 'Resident added — please capture required signatures');
        resetForm();
        fetchResidents();
        // Prompt staff to capture required signatures so they aren't missed.
        // Delay opening so the intake dialog fully closes first (avoids a Radix
        // double-dialog race that can stop the consent prompt from appearing).
        setConsentResident(response.data);
        setConsentEnforce(true);
        setTimeout(() => setShowConsentCapture(true), 350);
      }
    } catch (error) {
      console.error('Error saving resident:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save resident');
    }
  };

  const handleStatusChange = async (residentId, newStatus) => {
    try {
      await axios.put(`${API}/residents/${residentId}`, { status: newStatus });
      toast.success(`Resident moved to ${newStatus} list`);
      fetchResidents();
    } catch (error) {
      console.error('Error updating resident status:', error);
      toast.error('Failed to update resident status');
    }
  };

  const handleDepartResident = async () => {
    if (!selectedResidentForAction) return;
    
    try {
      const response = await axios.put(`${API}/residents/${selectedResidentForAction.id}/depart`, departData);
      toast.success('Resident departed successfully');
      
      // Use backend response data for accurate print information
      const backendData = response.data;
      
      // Generate print data for departure form using backend response
      setPrintData({
        type: 'departure',
        resident: selectedResidentForAction,
        data: {
          departure_reason: backendData.departure_reason || departData.departure_reason,
          departure_notes: backendData.departure_notes || departData.departure_notes,
          departure_date: backendData.departure_date || new Date().toISOString(),
          readmission_date: backendData.readmission_date,
          message: backendData.message || 'Departure processed successfully',
          forwarding_address: backendData.forwarding_address || departData.forwarding_address,
          destination_shelter_type: backendData.destination_shelter_type || departData.destination_shelter_type,
          staff_name: user?.full_name || 'Current User'
        }
      });
      
      resetDepartDialog();
      fetchResidents();
      setShowPrintDialog(true);
    } catch (error) {
      console.error('Error departing resident:', error);
      toast.error(error.response?.data?.detail || 'Failed to depart resident');
    }
  };

  const handlePrintResidents = async () => {
    try {
      const response = await axios.get(`${API}/residents/print-list?status=${selectedStatus}`);
      const printData = escapeDeep(response.data);
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${printData.status} Residents List</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .info { margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Shelter Care Connect - ${printData.status} Residents</h1>
            <div class="info">
              <p><strong>Total Residents:</strong> ${printData.total_count}</p>
              <p><strong>Generated:</strong> ${printData.generated_at}</p>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Room</th>
                <th>Days Stayed</th>
                <th>Intake Date</th>
                <th>Phone</th>
                <th>Emergency Contact</th>
                <th>Emergency Phone</th>
              </tr>
            </thead>
            <tbody>
              ${printData.residents.map(resident => `
                <tr>
                  <td>${resident.name}</td>
                  <td>${resident.room}</td>
                  <td>${resident.stay_days}</td>
                  <td>${resident.intake_date}</td>
                  <td>${resident.phone}</td>
                  <td>${resident.emergency_contact}</td>
                  <td>${resident.emergency_phone}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            <p>Printed from Shelter Care Connect Management System</p>
          </div>
        </body>
        </html>
      `;
      
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    } catch (error) {
      console.error('Error generating print list:', error);
      toast.error('Failed to generate print list');
    }
  };

  const handleBanResident = async () => {
    if (!selectedResidentForAction) return;
    
    try {
      const response = await axios.put(`${API}/residents/${selectedResidentForAction.id}/ban`, banData);
      toast.success('Resident banned successfully');
      
      // Generate print data for ban form
      setPrintData({
        type: 'ban',
        resident: selectedResidentForAction,
        data: {
          ...banData,
          ban_date: new Date().toLocaleString(),
          staff_name: response.data.banned_by
        }
      });
      
      resetBanDialog();
      fetchResidents();
      setShowPrintDialog(true);
    } catch (error) {
      console.error('Error banning resident:', error);
      toast.error(error.response?.data?.detail || 'Failed to ban resident');
    }
  };

  const handlePrintResidentProfile = async (resident) => {
    // Fetch each independently so one failing endpoint doesn't drop the other datasets
    let caseNotes = [];
    let incidentReports = [];
    let consents = [];
    try {
      const r = await axios.get(`${API}/case-notes/resident/${resident.id}`);
      caseNotes = r.data || [];
    } catch (e) { console.warn('case-notes fetch failed:', e?.response?.status); }
    try {
      const r = await axios.get(`${API}/incident-reports?resident_id=${resident.id}`);
      incidentReports = r.data || [];
    } catch (e) { console.warn('incident-reports fetch failed:', e?.response?.status); }
    try {
      const r = await axios.get(`${API}/consent-records?resident_id=${resident.id}`);
      consents = r.data || [];
    } catch (e) { console.warn('consent-records fetch failed:', e?.response?.status); }

    setPrintResident({ ...resident, caseNotes, incidentReports, consents });
    setPrintType('resident_profile');
    setShowResidentPrintDialog(true);
  };

  const handlePrintCombinedReport = async (resident) => {
    // Fetch each independently for the same resilience as the profile print
    let caseNotes = [];
    let incidentReports = [];
    let consents = [];
    try {
      const r = await axios.get(`${API}/case-notes/resident/${resident.id}`);
      caseNotes = r.data || [];
    } catch (e) { console.warn('case-notes fetch failed:', e?.response?.status); }
    try {
      const r = await axios.get(`${API}/incident-reports?resident_id=${resident.id}`);
      incidentReports = r.data || [];
    } catch (e) { console.warn('incident-reports fetch failed:', e?.response?.status); }
    try {
      const r = await axios.get(`${API}/consent-records?resident_id=${resident.id}`);
      consents = r.data || [];
    } catch (e) { console.warn('consent-records fetch failed:', e?.response?.status); }

    setPrintResident({ ...resident, caseNotes, incidentReports, consents });
    setPrintType('combined_report');
    setShowResidentPrintDialog(true);
  };

  const handleReadmitResident = async (residentId) => {
    try {
      await axios.put(`${API}/residents/${residentId}/readmit`);
      toast.success('Resident readmitted successfully');
      fetchResidents();
    } catch (error) {
      console.error('Error readmitting resident:', error);
      toast.error(error.response?.data?.detail || 'Failed to readmit resident');
    }
  };

  const handleAdminOverrideReadmit = async (residentId, residentName) => {
    if (!window.confirm(`ADMIN OVERRIDE: Are you sure you want to readmit ${residentName} immediately, bypassing all waiting periods and restrictions?\n\nThis action will be logged.`)) {
      return;
    }
    
    try {
      const response = await axios.put(`${API}/residents/${residentId}/readmit-override`);
      toast.success('Resident readmitted with admin override');
      console.log('Admin override used:', response.data);
      fetchResidents();
    } catch (error) {
      console.error('Error with admin override readmit:', error);
      toast.error(error.response?.data?.detail || 'Failed to readmit resident with override');
    }
  };


  const resetDepartDialog = () => {
    setDepartData({
      departure_reason: 'departed',
      departure_notes: '',
      forwarding_address: '',
      destination_shelter_type: 'emergency'
    });
    setShowDepartDialog(false);
    setSelectedResidentForAction(null);
  };

  const resetBanDialog = () => {
    setBanData({
      ban_reason: '',
      ban_notes: ''
    });
    setShowBanDialog(false);
    setSelectedResidentForAction(null);
  };

  const resetForm = () => {
    setFormData({
      // Basic Information
      first_name: '',
      last_name: '',
      date_of_birth: '',
      intake_date: new Date().toISOString().split('T')[0],
      social_security_number: '',
      insurance_type: '',
      primary_language: '',
      
      // Demographics
      gender: '',
      race: '',
      ethnicity: '',
      
      // Status Information
      veteran_status: '',
      disability_status: '',
      marital_status: '',
      household_composition: '',
      family_members: [],
      
      // Contact Information
      phone: '',
      email: '',
      emergency_contact: '',
      emergency_phone: '',
      
      // Homeless Status and Living Situation
      previous_living_situation: '',
      homeless_duration: '',
      times_homeless_past_three_years: '',
      
      // Income and Benefits
      employment_status: '',
      income_sources: [],
      monthly_income: '',
      receives_benefits: '',
      income_food_stamps: false,
      income_tanf: false,
      income_social_security: false,
      income_employment_ft: false,
      income_employment_pt: false,
      income_ssa: false,
      income_ssi: false,
      income_ssdi: false,
      income_other: false,
      income_other_amount: '',
      
      // Health and Disability Information
      medical_notes: '',
      special_needs: '',
      dietary_restrictions: '',
    other_allergies: '',
      mental_health_issues: '',
      substance_abuse_issues: '',
      disability_drug_problem: false,
      disability_alcohol_problem: false,
      disability_physical_mental_health: false,
      disability_developmental: false,
      disability_physical_handicap: false,
      disability_hiv_aids: false,
      medical_drug_problem: false,
      medical_alcohol_problem: false,
      medical_physical_health: false,
      medical_communicable_disease: false,
      medical_learning_disability: false,
      medical_add_adhd: false,
      medical_diabetes: false,
      medical_seizures: false,
      
      // Housing History
      education_level: '',
      housing_history: '',
      will_relocate: '',
      housing_priority_list: '',
      
      // Domestic Violence
      domestic_violence_survivor: '',
      
      // Program Enrollment Information
      case_manager: '',
      service_plan_goals: '',
      
      // Additional Information
      additional_notes: '',
      referral_source: '',
      cpd_clear: '',
      drug_screen: '',
      criminal_convictions: '',
      // PATH Program (SAMHSA)
      path_enrolled: '',
      path_status: '',
      path_date_of_engagement: '',
      path_date_of_status_determination: '',
      path_connection_soar: '',
      path_current_living_situation: '',
      path_services_provided: '',
      path_referrals_provided: ''
    });
    setEditingResident(null);
    setShowAddDialog(false);
    setAddMode('resident'); // Reset to resident mode
  };

  const resetStaffForm = () => {
    setStaffFormData({
      full_name: '',
      username: '',
      email: '',
      password: '',
      role: 'staff'
    });
    setShowAddDialog(false);
    setAddMode('resident'); // Reset to resident mode
  };

  const handleStaffSubmit = async () => {
    try {
      // Set default email if empty
      const staffData = {
        ...staffFormData,
        email: staffFormData.email || `${staffFormData.username}@shelter.placeholder`
      };
      
      await axios.post(`${API}/staff`, staffData);
      toast.success(`Staff member "${staffData.full_name}" added successfully!`);
      resetStaffForm();
      fetchStaff(); // Refresh staff list
    } catch (error) {
      console.error('Error adding staff:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to add staff member';
      if (errorMessage.includes('Username already exists')) {
        toast.error('This username is already taken. Please choose a different username.');
      } else if (errorMessage.includes('Email already exists')) {
        toast.error('This email is already registered. Please use a different email.');
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const fetchStaff = async () => {
    try {
      console.log('Fetching staff...');
      const response = await axios.get(`${API}/staff`);
      console.log('Staff response:', response.data);
      setAllStaff(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
      toast.error('Failed to load staff members');
    }
  };

  const handleDeleteStaff = async () => {
    console.log('Delete staff called with:', staffToDelete);
    if (!staffToDelete) {
      console.log('No staff to delete');
      return;
    }
    
    try {
      console.log('Attempting to delete staff with ID:', staffToDelete.id);
      const response = await axios.delete(`${API}/staff/${staffToDelete.id}`);
      console.log('Delete response:', response);
      toast.success(`Staff member "${staffToDelete.full_name}" has been removed.`);
      setShowDeleteStaffDialog(false);
      setStaffToDelete(null);
      fetchStaff(); // Refresh staff list
    } catch (error) {
      console.error('Error deleting staff:', error);
      console.error('Error response:', error.response);
      toast.error(`Failed to delete staff member: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleDeleteResident = async () => {
    if (!residentToDelete) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/residents/${residentToDelete.id}`);
      toast.success(`Resident "${residentToDelete.first_name} ${residentToDelete.last_name}" has been permanently deleted.`);
      setShowDeleteResidentDialog(false);
      setResidentToDelete(null);
      fetchResidents(); // Refresh resident list
    } catch (error) {
      console.error('Error deleting resident:', error);
      toast.error(`Failed to delete resident: ${error.response?.data?.detail || error.message}`);
    }
  };

  const printResidentData = (rawResident) => {
    const resident = escapeDeep(rawResident);
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    // Generate printable HTML content
    const printContent = generatePrintableResidentData(resident);
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Resident Data - ${resident.first_name} ${resident.last_name}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              line-height: 1.4;
              max-width: 8.5in;
              margin: 0 auto;
              padding: 0.5in;
              color: #000;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
              margin-bottom: 20px;
            }
            .section {
              margin-bottom: 15px;
              page-break-inside: avoid;
            }
            .section-title {
              font-weight: bold;
              font-size: 14px;
              color: #333;
              border-bottom: 1px solid #ccc;
              padding-bottom: 2px;
              margin-bottom: 8px;
            }
            .field-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px;
              margin-bottom: 10px;
            }
            .field {
              margin-bottom: 5px;
            }
            .field-label {
              font-weight: bold;
              display: inline-block;
              width: 120px;
            }
            .field-value {
              display: inline-block;
            }
            .checkbox-list {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 5px;
              margin-left: 20px;
            }
            .checkbox-item {
              font-size: 12px;
            }
            .textarea-field {
              margin-top: 5px;
              padding: 5px;
              border: 1px solid #ccc;
              background: #f9f9f9;
              white-space: pre-wrap;
            }
            @media print {
              body { margin: 0; padding: 0.5in; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = function() {
      printWindow.focus();
      printWindow.print();
      // Close window after printing (optional)
      // printWindow.close();
    };
  };

  const generatePrintableResidentData = (resident) => {
    const formatDate = (dateString) => {
      if (!dateString) return 'Not provided';
      return new Date(dateString).toLocaleDateString('en-US');
    };

    const formatBoolean = (value) => {
      if (value === true) return '✓ Yes';
      if (value === false) return '✗ No';
      return 'Not specified';
    };

    const getIncomeTypes = () => {
      const incomeTypes = [];
      if (resident.income_food_stamps) incomeTypes.push('Food Stamps');
      if (resident.income_tanf) incomeTypes.push('TANF');
      if (resident.income_social_security) incomeTypes.push('Social Security');
      if (resident.income_employment_ft) incomeTypes.push('Employment FT');
      if (resident.income_employment_pt) incomeTypes.push('Employment PT');
      if (resident.income_ssa) incomeTypes.push('SSA');
      if (resident.income_ssi) incomeTypes.push('SSI');
      if (resident.income_ssdi) incomeTypes.push('SSDI');
      if (resident.income_other) incomeTypes.push(`Other (${resident.income_other_amount || 'Amount not specified'})`);
      return incomeTypes.length > 0 ? incomeTypes.join(', ') : 'None specified';
    };

    const getDisabilityIssues = () => {
      const issues = [];
      if (resident.disability_drug_problem) issues.push('Drug Problem');
      if (resident.disability_alcohol_problem) issues.push('Alcohol Problem');
      if (resident.disability_physical_mental_health) issues.push('Physical/Mental Health');
      if (resident.disability_developmental) issues.push('Developmental Disability');
      if (resident.disability_physical_handicap) issues.push('Physical Handicap');
      if (resident.disability_hiv_aids) issues.push('HIV/AIDS');
      return issues.length > 0 ? issues.join(', ') : 'None specified';
    };

    const getMedicalIssues = () => {
      const issues = [];
      if (resident.medical_drug_problem) issues.push('Drug Problem');
      if (resident.medical_alcohol_problem) issues.push('Alcohol Problem');
      if (resident.medical_physical_health) issues.push('Physical Health');
      if (resident.medical_communicable_disease) issues.push('Communicable Disease');
      if (resident.medical_learning_disability) issues.push('Learning Disability');
      if (resident.medical_add_adhd) issues.push('ADD/ADHD');
      if (resident.medical_diabetes) issues.push('Diabetes');
      if (resident.medical_seizures) issues.push('Seizures');
      return issues.length > 0 ? issues.join(', ') : 'None specified';
    };

    return `
      <div class="header">
        <h1>Shelter Care Connect - Resident Data Report</h1>
        <h2>${resident.first_name} ${resident.last_name}</h2>
        <p>Report Generated: ${new Date().toLocaleDateString('en-US')} at ${new Date().toLocaleTimeString('en-US')}</p>
      </div>

      <div class="section">
        <div class="section-title">Basic Information</div>
        <div class="field-grid">
          <div class="field">
            <span class="field-label">Full Name:</span>
            <span class="field-value">${resident.first_name} ${resident.last_name}</span>
          </div>
          <div class="field">
            <span class="field-label">Date of Birth:</span>
            <span class="field-value">${formatDate(resident.date_of_birth)}</span>
          </div>
          <div class="field">
            <span class="field-label">SSN:</span>
            <span class="field-value">${resident.social_security_number || 'Not provided'}</span>
          </div>
          <div class="field">
            <span class="field-label">Gender:</span>
            <span class="field-value">${(resident.gender || 'Not specified').replace(/_/g, ' ')}</span>
          </div>
          <div class="field">
            <span class="field-label">Insurance:</span>
            <span class="field-value">${resident.insurance_type || 'Not specified'}</span>
          </div>
          <div class="field">
            <span class="field-label">Primary Language:</span>
            <span class="field-value">${resident.primary_language || 'Not specified'}</span>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Intake Information</div>
        <div class="field-grid">
          <div class="field">
            <span class="field-label">Intake Date:</span>
            <span class="field-value">${formatDate(resident.intake_date)}</span>
          </div>
          <div class="field">
            <span class="field-label">Days Stayed:</span>
            <span class="field-value">${resident.stay_length_days} days</span>
          </div>
          <div class="field">
            <span class="field-label">Status:</span>
            <span class="field-value">${resident.status}</span>
          </div>
          <div class="field">
            <span class="field-label">Room Assignment:</span>
            <span class="field-value">${resident.room_id || 'Not assigned'}</span>
          </div>
          <div class="field">
            <span class="field-label">CPD Clear:</span>
            <span class="field-value">${resident.cpd_clear || 'Not specified'}</span>
          </div>
          <div class="field">
            <span class="field-label">Drug Screen:</span>
            <span class="field-value">${resident.drug_screen || 'Not specified'}</span>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Demographics</div>
        <div class="field-grid">
          <div class="field">
            <span class="field-label">Gender:</span>
            <span class="field-value">${resident.gender || 'Not specified'}</span>
          </div>
          <div class="field">
            <span class="field-label">Race:</span>
            <span class="field-value">${resident.race || 'Not specified'}</span>
          </div>
          <div class="field">
            <span class="field-label">Ethnicity:</span>
            <span class="field-value">${resident.ethnicity || 'Not specified'}</span>
          </div>
          <div class="field">
            <span class="field-label">Veteran Status:</span>
            <span class="field-value">${resident.veteran_status || 'Not specified'}</span>
          </div>
          <div class="field">
            <span class="field-label">Marital Status:</span>
            <span class="field-value">${resident.marital_status || 'Not specified'}</span>
          </div>
          <div class="field">
            <span class="field-label">Education Level:</span>
            <span class="field-value">${resident.education_level || 'Not specified'}</span>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Contact Information</div>
        <div class="field-grid">
          <div class="field">
            <span class="field-label">Phone:</span>
            <span class="field-value">${resident.phone || 'Not provided'}</span>
          </div>
          <div class="field">
            <span class="field-label">Email:</span>
            <span class="field-value">${resident.email || 'Not provided'}</span>
          </div>
          <div class="field">
            <span class="field-label">Emergency Contact:</span>
            <span class="field-value">${resident.emergency_contact || 'Not provided'}</span>
          </div>
          <div class="field">
            <span class="field-label">Emergency Phone:</span>
            <span class="field-value">${resident.emergency_phone || 'Not provided'}</span>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Financial Information</div>
        <div class="field">
          <span class="field-label">Employment Status:</span>
          <span class="field-value">${resident.employment_status || 'Not specified'}</span>
        </div>
        <div class="field">
          <span class="field-label">Monthly Income:</span>
          <span class="field-value">$${resident.monthly_income || 'Not specified'}</span>
        </div>
        <div class="field">
          <span class="field-label">Income Types:</span>
          <div class="textarea-field">${getIncomeTypes()}</div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Health & Disability Information</div>
        <div class="field">
          <span class="field-label">Disability Issues:</span>
          <div class="textarea-field">${getDisabilityIssues()}</div>
        </div>
        <div class="field">
          <span class="field-label">Medical Issues:</span>
          <div class="textarea-field">${getMedicalIssues()}</div>
        </div>
        <div class="field">
          <span class="field-label">Special Needs:</span>
          <div class="textarea-field">${resident.special_needs || 'None specified'}</div>
        </div>
        <div class="field">
          <span class="field-label">Dietary Restrictions & Food Allergies:</span>
          <div class="textarea-field">${resident.dietary_restrictions || 'None specified'}</div>
        </div>
        <div class="field">
          <span class="field-label">Other Allergies:</span>
          <div class="textarea-field">${resident.other_allergies || resident.food_allergies || 'None specified'}</div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Housing Information</div>
        <div class="field-grid">
          <div class="field">
            <span class="field-label">Previous Living:</span>
            <span class="field-value">${resident.previous_living_situation || 'Not specified'}</span>
          </div>
          <div class="field">
            <span class="field-label">Homeless Duration:</span>
            <span class="field-value">${resident.homeless_duration || 'Not specified'}</span>
          </div>
          <div class="field">
            <span class="field-label">Will Relocate:</span>
            <span class="field-value">${resident.will_relocate || 'Not specified'}</span>
          </div>
          <div class="field">
            <span class="field-label">Housing Priority:</span>
            <span class="field-value">${resident.housing_priority_list || 'Not specified'}</span>
          </div>
        </div>
        <div class="field">
          <span class="field-label">Housing History:</span>
          <div class="textarea-field">${resident.housing_history || 'Not provided'}</div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Additional Information</div>
        <div class="field">
          <span class="field-label">Criminal Convictions:</span>
          <span class="field-value">${resident.criminal_convictions || 'Not specified'}</span>
        </div>
        <div class="field">
          <span class="field-label">Case Manager:</span>
          <span class="field-value">${resident.case_manager || 'Not assigned'}</span>
        </div>
        <div class="field">
          <span class="field-label">Referral Source:</span>
          <span class="field-value">${resident.referral_source || 'Not specified'}</span>
        </div>
        <div class="field">
          <span class="field-label">Service Plan Goals:</span>
          <div class="textarea-field">${resident.service_plan_goals || 'Not provided'}</div>
        </div>
        <div class="field">
          <span class="field-label">Additional Notes:</span>
          <div class="textarea-field">${resident.additional_notes || 'None'}</div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Report Information</div>
        <p><strong>This report was generated from Shelter Care Connect resident management system.</strong></p>
        <p><strong>All information is confidential and should be handled according to shelter policies and applicable privacy laws.</strong></p>
      </div>
    `;
  };

  const startEdit = (resident) => {
    // RBAC: general staff cannot edit records post-intake — they submit a change request
    if (user?.role === 'general_staff') {
      openChangeRequest(resident);
      return;
    }
    setFormData({
      // Basic Information
      first_name: resident.first_name || '',
      last_name: resident.last_name || '',
      date_of_birth: resident.date_of_birth || '',
      intake_date: resident.intake_date ? new Date(resident.intake_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      social_security_number: resident.social_security_number || '',
      
      // Demographics
      gender: resident.gender || '',
      race: resident.race || '',
      ethnicity: resident.ethnicity || '',
      
      // Status Information
      veteran_status: resident.veteran_status || '',
      disability_status: resident.disability_status || '',
      marital_status: resident.marital_status || '',
      household_composition: resident.household_composition || '',
      
      // Contact Information
      phone: resident.phone || '',
      email: resident.email || '',
      emergency_contact: resident.emergency_contact || '',
      emergency_phone: resident.emergency_phone || '',
      
      // Homeless Status and Living Situation
      previous_living_situation: resident.previous_living_situation || '',
      homeless_duration: resident.homeless_duration || '',
      times_homeless_past_three_years: resident.times_homeless_past_three_years || '',
      
      // Income and Benefits
      employment_status: resident.employment_status || '',
      income_sources: resident.income_sources || [],
      monthly_income: resident.monthly_income || '',
      receives_benefits: resident.receives_benefits || '',
      
      // Health and Disability Information
      medical_notes: resident.medical_notes || '',
      special_needs: resident.special_needs || '',
      dietary_restrictions: resident.dietary_restrictions || '',
      other_allergies: resident.other_allergies || resident.food_allergies || '',
      mental_health_issues: resident.mental_health_issues || '',
      substance_abuse_issues: resident.substance_abuse_issues || '',
      
      // Housing History
      education_level: resident.education_level || '',
      housing_history: resident.housing_history || '',
      
      // Domestic Violence
      domestic_violence_survivor: resident.domestic_violence_survivor || '',
      
      // Program Enrollment Information
      case_manager: resident.case_manager || '',
      service_plan_goals: resident.service_plan_goals || '',
      
      // Additional Information
      additional_notes: resident.additional_notes || '',
      referral_source: resident.referral_source || '',
      cpd_clear: resident.cpd_clear || '',
      drug_screen: resident.drug_screen || '',
      // PATH Program (SAMHSA)
      path_enrolled: resident.path_enrolled || '',
      path_status: resident.path_status || '',
      path_date_of_engagement: resident.path_date_of_engagement || '',
      path_date_of_status_determination: resident.path_date_of_status_determination || '',
      path_connection_soar: resident.path_connection_soar || '',
      path_current_living_situation: resident.path_current_living_situation || '',
      path_services_provided: resident.path_services_provided || '',
      path_referrals_provided: resident.path_referrals_provided || ''
    });
    setEditingResident(resident);
    setShowAddDialog(true);
  };

  const getStayColor = (days) => {
    if (days <= 7) return 'stay-short';
    if (days <= 30) return 'stay-medium';
    return 'stay-long';
  };

  // Derive age + Adult/Minor from date of birth (minor = under 18)
  const getAgeInfo = (dob) => {
    if (!dob) return null;
    const b = new Date(dob);
    if (isNaN(b.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - b.getFullYear();
    const m = now.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
    if (age < 0 || age > 130) return null;
    return { age, isMinor: age < 18 };
  };

  const getStayLabel = (days) => {
    if (days <= 7) return `${days} days - Short Stay`;
    if (days <= 30) return `${days} days - Medium Stay`;
    return `${days} days - Long Stay`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isEligibleForReadmission = (resident) => {
    if (resident.status !== 'past') return false;
    if (resident.eligible_for_readmission) return true;
    if (resident.readmission_date) {
      return new Date(resident.readmission_date) <= new Date();
    }
    return false;
  };

  const filteredResidents = residents.filter(resident =>
    `${resident.first_name} ${resident.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (resident.phone && resident.phone.includes(searchTerm))
  );

  // Order so family/household members render consecutively (head → spouse → child → other),
  // enabling the household grouping divider + accent on the Residents page.
  const orderedResidents = (() => {
    const relRank = { head: 0, spouse: 1, child: 2, other: 3 };
    const seen = new Set();
    const out = [];
    for (const r of filteredResidents) {
      if (r.household_id) {
        if (seen.has(r.household_id)) continue;
        seen.add(r.household_id);
        const members = filteredResidents
          .filter(x => x.household_id === r.household_id)
          .sort((a, b) => (relRank[a.relationship_to_head] ?? 9) - (relRank[b.relationship_to_head] ?? 9));
        out.push(...members);
      } else {
        out.push(r);
      }
    }
    return out;
  })();
  const householdSizes = orderedResidents.reduce((acc, r) => {
    if (r.household_id) acc[r.household_id] = (acc[r.household_id] || 0) + 1;
    return acc;
  }, {});

  const statusConfig = {
    current: {
      title: 'Current Residents',
      description: 'Residents currently staying at the shelter',
      icon: CheckCircle,
      color: 'text-green-600'
    },
    past: {
      title: 'Past Residents',
      description: 'Residents who have left the shelter',
      icon: Clock,
      color: 'text-gray-600'
    },
    banned: {
      title: 'Banned Residents',
      description: 'Residents who are not allowed to return',
      icon: XCircle,
      color: 'text-red-600'
    }
  };

  const currentConfig = statusConfig[selectedStatus];
  const StatusIcon = currentConfig.icon;

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <Users className="h-8 w-8 text-indigo-600" />
            <span>Resident Management</span>
          </h1>
          <p className="text-gray-600 mt-1">Manage shelter residents and their information</p>
        </div>
        
        <div className="flex space-x-2">
          <Button 
            onClick={handlePrintResidents}
            variant="outline" 
            className="border-indigo-600 text-indigo-600 hover:bg-indigo-50 flex items-center space-x-2"
          >
            <Printer className="h-4 w-4" />
            <span>Print List</span>
          </Button>

          <ImportSpreadsheet
            kind={selectedStatus === 'banned' ? 'banned' : 'residents'}
            onImported={fetchResidents}
          />
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center space-x-2">
                <Plus className="h-4 w-4" />
                <span>Add Person</span>
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingResident ? 'Edit Resident' : 
                 addMode === 'staff' ? 'Add Staff Member' : 'HMIS-Compliant Resident Intake'}
              </DialogTitle>
              <DialogDescription>
                {editingResident ? 'Update resident information' : 
                 addMode === 'staff' ? 'Add a new staff member to the system' : 
                 'Complete comprehensive intake form with HMIS-compliant data collection'}
              </DialogDescription>
            </DialogHeader>
            
            {/* Mode Toggle - Only show when not editing */}
            {!editingResident && (
              <div className="flex space-x-2 mb-4">
                <Button
                  type="button"
                  variant={addMode === 'resident' ? 'default' : 'outline'}
                  onClick={() => setAddMode('resident')}
                  className="flex-1"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Add Resident
                </Button>
                <Button
                  type="button"
                  variant={addMode === 'staff' ? 'default' : 'outline'}
                  onClick={() => setAddMode('staff')}
                  className="flex-1"
                >
                  <UserCheck className="h-4 w-4 mr-2" />
                  Add Staff
                </Button>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {addMode === 'staff' ? (
                // STAFF FORM AND MANAGEMENT
                <div className="space-y-6">
                  {/* Add Staff Form */}
                  <div className="space-y-4 border-b pb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Add New Staff Member</h3>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Full Name *</label>
                      <Input
                        required
                        value={staffFormData.full_name}
                        onChange={(e) => setStaffFormData({...staffFormData, full_name: e.target.value})}
                        placeholder="Enter full name"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Username *</label>
                      <Input
                        required
                        value={staffFormData.username}
                        onChange={(e) => setStaffFormData({...staffFormData, username: e.target.value})}
                        placeholder="Enter username for login"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Email</label>
                      <Input
                        type="email"
                        value={staffFormData.email}
                        onChange={(e) => setStaffFormData({...staffFormData, email: e.target.value})}
                        placeholder="Enter email address (optional)"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Password *</label>
                      <Input
                        type="password"
                        required
                        value={staffFormData.password}
                        onChange={(e) => setStaffFormData({...staffFormData, password: e.target.value})}
                        placeholder="Enter password"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Role *</label>
                      <Select value={staffFormData.role} onValueChange={(value) => setStaffFormData({...staffFormData, role: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="staff">Staff - General operations, cannot delete</SelectItem>
                          <SelectItem value="admin">Administrator - Full access including deleting</SelectItem>
                          <SelectItem value="case_worker">Case Manager - Social services</SelectItem>
                          <SelectItem value="general_staff">General Staff - First responder view, no post-intake edits</SelectItem>
                          <SelectItem value="kitchen_staff">Kitchen Staff - Food services</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex justify-end space-x-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                        Cancel
                      </Button>
                      <Button type="button" onClick={handleStaffSubmit} className="bg-indigo-600 hover:bg-indigo-700">
                        Add Staff Member
                      </Button>
                    </div>
                  </div>

                  {/* Current Staff List */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-semibold text-gray-900">Current Staff Members</h3>
                      <Badge variant="secondary">{allStaff.length} Total</Badge>
                    </div>
                    
                    <div className="grid gap-3 max-h-60 overflow-y-auto">
                      {allStaff.length > 0 ? allStaff.map((staff) => (
                        <div key={staff.id} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <div>
                                <p className="font-medium text-gray-900">{staff.full_name}</p>
                                <p className="text-sm text-gray-500">
                                  @{staff.username} • {staff.role}
                                  {staff.email && ` • ${staff.email}`}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Only show delete button to admins */}
                          {user?.role === 'admin' && (
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              onClick={() => {
                                setStaffToDelete(staff);
                                setShowDeleteStaffDialog(true);
                              }}
                              className="ml-3"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      )) : (
                        <div className="text-center py-8 text-gray-500">
                          <UserCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>Loading staff members... ({allStaff.length} loaded)</p>
                          <p className="text-xs mt-2">If this persists, try refreshing the page</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                // EXISTING RESIDENT FORM
                <div>
                  <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-6">
                  <TabsTrigger value="basic">Basic Info</TabsTrigger>
                  <TabsTrigger value="demographics">Demographics</TabsTrigger>
                  <TabsTrigger value="contact">Contact & Living</TabsTrigger>
                  <TabsTrigger value="financial">Financial & Health</TabsTrigger>
                  <TabsTrigger value="additional">Additional Info</TabsTrigger>
                  <TabsTrigger value="path" data-testid="tab-path">PATH</TabsTrigger>
                </TabsList>

                {/* Basic Information Tab */}
                <TabsContent value="basic" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">First Name *</label>
                      <Input
                        required
                        value={formData.first_name}
                        onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                        placeholder="Enter first name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Last Name *</label>
                      <Input
                        required
                        value={formData.last_name}
                        onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                        placeholder="Enter last name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Date of Birth</label>
                      <Input
                        type="date"
                        value={formData.date_of_birth}
                        onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Intake Date *</label>
                      <Input
                        type="date"
                        value={formData.intake_date}
                        onChange={(e) => setFormData({...formData, intake_date: e.target.value})}
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Date the resident first arrived at the shelter</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Social Security Number (Optional)</label>
                      <Input
                        type="password"
                        value={formData.social_security_number}
                        onChange={(e) => setFormData({...formData, social_security_number: e.target.value})}
                        placeholder="XXX-XX-XXXX"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Gender *</label>
                      <Select value={formData.gender} onValueChange={(value) => setFormData({...formData, gender: value})} required>
                        <SelectTrigger data-testid="resident-gender">
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="transgender_mtf">Transgender Male-to-Female</SelectItem>
                          <SelectItem value="transgender_ftm">Transgender Female-to-Male</SelectItem>
                          <SelectItem value="non_binary">Non-Binary</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                          <SelectItem value="declined">Client Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Insurance Type</label>
                      <Select value={formData.insurance_type} onValueChange={(value) => setFormData({...formData, insurance_type: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select insurance type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="medicare">Medicare</SelectItem>
                          <SelectItem value="medicaid">Medicaid</SelectItem>
                          <SelectItem value="private">Private</SelectItem>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="va">VA</SelectItem>
                          <SelectItem value="kid_care">Kid Care</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Primary Language</label>
                      <Select value={formData.primary_language} onValueChange={(value) => setFormData({...formData, primary_language: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select primary language" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="english">English</SelectItem>
                          <SelectItem value="spanish">Spanish</SelectItem>
                          <SelectItem value="french">French</SelectItem>
                          <SelectItem value="german">German</SelectItem>
                          <SelectItem value="chinese">Chinese</SelectItem>
                          <SelectItem value="arabic">Arabic</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Family / Household Members — create linked records without separate intakes */}
                  {!editingResident && (
                    <div className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50/40 p-4" data-testid="family-members-section">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-indigo-600" />
                          <span className="text-sm font-semibold text-gray-800">Family / Household Members</span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setFormData({ ...formData, family_members: [...(formData.family_members || []), { _key: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())), first_name: '', last_name: formData.last_name || '', date_of_birth: '', gender: '', social_security_number: '', relationship_to_head: 'child' }] })}
                          data-testid="add-family-member-btn"
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add member
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Add a spouse or children on this intake. Each gets their own record (so they can be assigned a bed) and is grouped as one household — no separate intake needed.</p>

                      {(formData.family_members || []).length === 0 && (
                        <p className="text-xs text-gray-400 mt-3 italic">No family members added. This is a single-person intake.</p>
                      )}

                      <div className="space-y-3 mt-3">
                        {(formData.family_members || []).map((m, i) => {
                          const upd = (field, val) => {
                            const next = [...formData.family_members];
                            next[i] = { ...next[i], [field]: val };
                            setFormData({ ...formData, family_members: next });
                          };
                          return (
                            <div key={m._key || i} className="rounded-md border border-gray-200 bg-white p-3" data-testid={`family-member-${i}`}>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <Input placeholder="First name" value={m.first_name} onChange={(e) => upd('first_name', e.target.value)} data-testid={`family-first-name-${i}`} />
                                <Input placeholder="Last name" value={m.last_name} onChange={(e) => upd('last_name', e.target.value)} data-testid={`family-last-name-${i}`} />
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                                <div>
                                  <label className="text-xs text-gray-500">Date of birth</label>
                                  <Input type="date" value={m.date_of_birth} onChange={(e) => upd('date_of_birth', e.target.value)} data-testid={`family-dob-${i}`} />
                                </div>
                                <div>
                                  <label className="text-xs text-gray-500">Gender</label>
                                  <Select value={m.gender} onValueChange={(v) => upd('gender', v)}>
                                    <SelectTrigger data-testid={`family-gender-${i}`}><SelectValue placeholder="Select" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="male">Male</SelectItem>
                                      <SelectItem value="female">Female</SelectItem>
                                      <SelectItem value="transgender_mtf">Transgender Male-to-Female</SelectItem>
                                      <SelectItem value="transgender_ftm">Transgender Female-to-Male</SelectItem>
                                      <SelectItem value="non_binary">Non-Binary</SelectItem>
                                      <SelectItem value="other">Other</SelectItem>
                                      <SelectItem value="declined">Client Declined</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <label className="text-xs text-gray-500">Relationship</label>
                                  <Select value={m.relationship_to_head} onValueChange={(v) => upd('relationship_to_head', v)}>
                                    <SelectTrigger data-testid={`family-relationship-${i}`}><SelectValue placeholder="Select" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="spouse">Spouse / Partner</SelectItem>
                                      <SelectItem value="child">Child</SelectItem>
                                      <SelectItem value="other">Other</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <div className="flex items-end justify-between gap-2 mt-2">
                                <div className="flex-1">
                                  <label className="text-xs text-gray-500">Social Security Number</label>
                                  <Input placeholder="SSN (optional)" value={m.social_security_number} onChange={(e) => upd('social_security_number', e.target.value)} data-testid={`family-ssn-${i}`} />
                                </div>
                                <Button type="button" variant="ghost" size="sm" className="text-red-600" onClick={() => setFormData({ ...formData, family_members: formData.family_members.filter((_, x) => x !== i) })} data-testid={`remove-family-member-${i}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                </TabsContent>

                {/* Demographics Tab */}
                <TabsContent value="demographics" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Race</label>
                      <Select value={formData.race} onValueChange={(value) => setFormData({...formData, race: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select race" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="white">White</SelectItem>
                          <SelectItem value="black_african_american">Black or African American</SelectItem>
                          <SelectItem value="asian">Asian</SelectItem>
                          <SelectItem value="american_indian_alaska_native">American Indian/Alaska Native</SelectItem>
                          <SelectItem value="native_hawaiian_pacific_islander">Native Hawaiian/Pacific Islander</SelectItem>
                          <SelectItem value="multi_racial">Multi-Racial</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                          <SelectItem value="declined">Client Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700">Ethnicity</label>
                      <Select value={formData.ethnicity} onValueChange={(value) => setFormData({...formData, ethnicity: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select ethnicity" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hispanic_latino">Hispanic or Latino</SelectItem>
                          <SelectItem value="non_hispanic_latino">Non-Hispanic/Non-Latino</SelectItem>
                          <SelectItem value="declined">Client Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700">Marital Status</label>
                      <Select value={formData.marital_status} onValueChange={(value) => setFormData({...formData, marital_status: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select marital status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single">Single</SelectItem>
                          <SelectItem value="married">Married</SelectItem>
                          <SelectItem value="divorced">Divorced</SelectItem>
                          <SelectItem value="separated">Separated</SelectItem>
                          <SelectItem value="widowed">Widowed</SelectItem>
                          <SelectItem value="partnered">Partnered</SelectItem>
                          <SelectItem value="declined">Client Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700">Education Level</label>
                      <Select value={formData.education_level} onValueChange={(value) => setFormData({...formData, education_level: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select education level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="less_than_high_school">Less than High School</SelectItem>
                          <SelectItem value="high_school_ged">High School/GED</SelectItem>
                          <SelectItem value="some_college">Some College</SelectItem>
                          <SelectItem value="associates_degree">Associate's Degree</SelectItem>
                          <SelectItem value="bachelors_degree">Bachelor's Degree</SelectItem>
                          <SelectItem value="graduate_degree">Graduate Degree</SelectItem>
                          <SelectItem value="declined">Client Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700">Veteran Status</label>
                      <Select value={formData.veteran_status} onValueChange={(value) => setFormData({...formData, veteran_status: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select veteran status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="declined">Client Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>

                {/* Contact & Living Tab */}
                <TabsContent value="contact" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Phone Number</label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Email Address</label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Emergency Contact</label>
                      <Input
                        value={formData.emergency_contact}
                        onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
                        placeholder="Emergency contact name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Emergency Phone</label>
                      <Input
                        type="tel"
                        value={formData.emergency_phone}
                        onChange={(e) => setFormData({...formData, emergency_phone: e.target.value})}
                        placeholder="(555) 987-6543"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Previous Living Situation</label>
                      <Select value={formData.previous_living_situation} onValueChange={(value) => setFormData({...formData, previous_living_situation: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select previous living situation" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="emergency_shelter">Emergency Shelter</SelectItem>
                          <SelectItem value="transitional_housing">Transitional Housing</SelectItem>
                          <SelectItem value="street">Street/Outdoors</SelectItem>
                          <SelectItem value="vehicle">Vehicle</SelectItem>
                          <SelectItem value="abandoned_building">Abandoned Building</SelectItem>
                          <SelectItem value="friend_family_couch">Friend/Family Couch</SelectItem>
                          <SelectItem value="hotel_motel">Hotel/Motel</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Duration of Homelessness</label>
                      <Select value={formData.homeless_duration} onValueChange={(value) => setFormData({...formData, homeless_duration: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select duration" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="less_than_7_days">Less than 7 days</SelectItem>
                          <SelectItem value="one_week_to_one_month">1 week to 1 month</SelectItem>
                          <SelectItem value="one_to_three_months">1 to 3 months</SelectItem>
                          <SelectItem value="three_to_six_months">3 to 6 months</SelectItem>
                          <SelectItem value="six_months_to_one_year">6 months to 1 year</SelectItem>
                          <SelectItem value="more_than_one_year">More than 1 year</SelectItem>
                          <SelectItem value="declined">Client Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Housing History</label>
                    <Textarea
                      value={formData.housing_history}
                      onChange={(e) => setFormData({...formData, housing_history: e.target.value})}
                      placeholder="Describe housing history, including barriers to housing..."
                      rows={3}
                    />
                  </div>
                  
                  {/* Housing Questions */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Will You Relocate? *</label>
                      <Select value={formData.will_relocate} onValueChange={(value) => setFormData({...formData, will_relocate: value})} required>
                        <SelectTrigger>
                          <SelectValue placeholder="Select answer" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Housing Priority List</label>
                      <Select value={formData.housing_priority_list} onValueChange={(value) => setFormData({...formData, housing_priority_list: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Do you want to be on housing priority list?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>

                {/* Financial & Health Tab */}
                <TabsContent value="financial" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Employment Status</label>
                      <Select value={formData.employment_status} onValueChange={(value) => setFormData({...formData, employment_status: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select employment status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="employed_full_time">Employed Full-Time</SelectItem>
                          <SelectItem value="employed_part_time">Employed Part-Time</SelectItem>
                          <SelectItem value="unemployed">Unemployed</SelectItem>
                          <SelectItem value="retired">Retired</SelectItem>
                          <SelectItem value="disabled">Disabled</SelectItem>
                          <SelectItem value="student">Student</SelectItem>
                          <SelectItem value="declined">Client Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Monthly Income</label>
                      <Input
                        type="number"
                        value={formData.monthly_income}
                        onChange={(e) => setFormData({...formData, monthly_income: e.target.value})}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Disability Status</label>
                      <Select value={formData.disability_status} onValueChange={(value) => setFormData({...formData, disability_status: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select disability status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="declined">Client Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Domestic Violence Survivor</label>
                      <Select value={formData.domestic_violence_survivor} onValueChange={(value) => setFormData({...formData, domestic_violence_survivor: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="declined">Client Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700">Receives Benefits</label>
                      <Select value={formData.receives_benefits} onValueChange={(value) => setFormData({...formData, receives_benefits: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select benefits status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="declined">Client Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700">Mental Health Issues</label>
                      <Select value={formData.mental_health_issues} onValueChange={(value) => setFormData({...formData, mental_health_issues: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="declined">Client Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700">Substance Abuse Issues</label>
                      <Select value={formData.substance_abuse_issues} onValueChange={(value) => setFormData({...formData, substance_abuse_issues: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="declined">Client Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700">Times Homeless (Past 3 Years)</label>
                      <Input
                        type="number"
                        value={formData.times_homeless_past_three_years}
                        onChange={(e) => setFormData({...formData, times_homeless_past_three_years: e.target.value})}
                        placeholder="Number of times homeless"
                        min="0"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700">Medical Notes</label>
                    <Textarea
                      value={formData.medical_notes}
                      onChange={(e) => setFormData({...formData, medical_notes: e.target.value})}
                      placeholder="Any medical conditions, medications, or health concerns..."
                      rows={3}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700">Dietary Restrictions & Food Allergies</label>
                    <Textarea
                      value={formData.dietary_restrictions}
                      onChange={(e) => setFormData({...formData, dietary_restrictions: e.target.value})}
                      placeholder="Food allergies (peanuts, shellfish, dairy, gluten) AND special diets (vegetarian, halal, low-sodium, diabetic)..."
                      rows={2}
                      data-testid="resident-dietary-restrictions"
                    />
                    <p className="text-xs text-gray-500 mt-1">Include food allergies here — these sync automatically to the Meals → Dietary Accommodations list for kitchen staff.</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Other Allergies</label>
                    <Textarea
                      value={formData.other_allergies}
                      onChange={(e) => setFormData({...formData, other_allergies: e.target.value})}
                      placeholder="Non-food allergies: medications, latex, cleaning supplies, chemicals, pets..."
                      rows={2}
                      data-testid="resident-other-allergies"
                    />
                  </div>
                  
                  {/* Income Types Section */}
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-700 mb-3 block">Income Types (Check all that apply)</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.income_food_stamps}
                          onChange={(e) => setFormData({...formData, income_food_stamps: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Food Stamps</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.income_tanf}
                          onChange={(e) => setFormData({...formData, income_tanf: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">TANF</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.income_social_security}
                          onChange={(e) => setFormData({...formData, income_social_security: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Social Security</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.income_employment_ft}
                          onChange={(e) => setFormData({...formData, income_employment_ft: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Employment FT</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.income_employment_pt}
                          onChange={(e) => setFormData({...formData, income_employment_pt: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Employment PT</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.income_ssa}
                          onChange={(e) => setFormData({...formData, income_ssa: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">SSA</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.income_ssi}
                          onChange={(e) => setFormData({...formData, income_ssi: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">SSI</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.income_ssdi}
                          onChange={(e) => setFormData({...formData, income_ssdi: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">SSDI</span>
                      </label>
                    </div>
                    <div className="flex items-center space-x-2 mt-3">
                      <input
                        type="checkbox"
                        checked={formData.income_other}
                        onChange={(e) => setFormData({...formData, income_other: e.target.checked})}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm">Other</span>
                      {formData.income_other && (
                        <Input
                          type="number"
                          value={formData.income_other_amount}
                          onChange={(e) => setFormData({...formData, income_other_amount: e.target.value})}
                          placeholder="Amount"
                          className="w-24"
                        />
                      )}
                    </div>
                  </div>
                  
                  {/* Disability Issues Section */}
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-700 mb-3 block">Disability Issues (Check all that apply)</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.disability_drug_problem}
                          onChange={(e) => setFormData({...formData, disability_drug_problem: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Drug Problem</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.disability_alcohol_problem}
                          onChange={(e) => setFormData({...formData, disability_alcohol_problem: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Alcohol Problem</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.disability_physical_mental_health}
                          onChange={(e) => setFormData({...formData, disability_physical_mental_health: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Physical/Mental Health</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.disability_developmental}
                          onChange={(e) => setFormData({...formData, disability_developmental: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Developmental Disability</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.disability_physical_handicap}
                          onChange={(e) => setFormData({...formData, disability_physical_handicap: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Physical Handicap</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.disability_hiv_aids}
                          onChange={(e) => setFormData({...formData, disability_hiv_aids: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">HIV or AIDS</span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Medical Issues Section */}
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-700 mb-3 block">Medical Issues (Check all that apply)</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.medical_drug_problem}
                          onChange={(e) => setFormData({...formData, medical_drug_problem: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Drug Problem</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.medical_alcohol_problem}
                          onChange={(e) => setFormData({...formData, medical_alcohol_problem: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Alcohol Problem</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.medical_physical_health}
                          onChange={(e) => setFormData({...formData, medical_physical_health: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Physical Health</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.medical_communicable_disease}
                          onChange={(e) => setFormData({...formData, medical_communicable_disease: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Communicable Disease</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.medical_learning_disability}
                          onChange={(e) => setFormData({...formData, medical_learning_disability: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Learning Disability</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.medical_add_adhd}
                          onChange={(e) => setFormData({...formData, medical_add_adhd: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">ADD/ADHD</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.medical_diabetes}
                          onChange={(e) => setFormData({...formData, medical_diabetes: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Diabetes</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.medical_seizures}
                          onChange={(e) => setFormData({...formData, medical_seizures: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">Seizures</span>
                      </label>
                    </div>
                  </div>
                </TabsContent>

                {/* Additional Information Tab */}
                <TabsContent value="additional" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Case Manager</label>
                      <Input
                        value={formData.case_manager}
                        onChange={(e) => setFormData({...formData, case_manager: e.target.value})}
                        placeholder="Assigned case manager name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Referral Source</label>
                      <Input
                        value={formData.referral_source}
                        onChange={(e) => setFormData({...formData, referral_source: e.target.value})}
                        placeholder="How did client find out about services?"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">CPD Clear *</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.cpd_clear}
                        onChange={(e) => setFormData({...formData, cpd_clear: e.target.value})}
                        required
                      >
                        <option value="">Select CPD Status</option>
                        <option value="yes">Yes - Clear</option>
                        <option value="no">No - Not Clear</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Drug Screen *</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.drug_screen}
                        onChange={(e) => setFormData({...formData, drug_screen: e.target.value})}
                        required
                      >
                        <option value="">Select Drug Screen Result</option>
                        <option value="pass">Pass</option>
                        <option value="fail">Fail</option>
                        <option value="case_manager_override">Case Manager Override</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Criminal Convictions</label>
                      <Select value={formData.criminal_convictions} onValueChange={(value) => setFormData({...formData, criminal_convictions: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select criminal conviction status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="misdemeanor">Misdemeanor</SelectItem>
                          <SelectItem value="felony">Felony</SelectItem>
                          <SelectItem value="both">Both</SelectItem>
                          <SelectItem value="client_refused">Client Refused</SelectItem>
                          <SelectItem value="none">None</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700">Service Plan Goals</label>
                    <Textarea
                      value={formData.service_plan_goals}
                      onChange={(e) => setFormData({...formData, service_plan_goals: e.target.value})}
                      placeholder="Goals and objectives for resident's service plan..."
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Special Needs & Accommodations</label>
                    <Textarea
                      value={formData.special_needs}
                      onChange={(e) => setFormData({...formData, special_needs: e.target.value})}
                      placeholder="Accessibility needs, accommodations, or other special requirements..."
                      rows={3}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700">Additional Notes</label>
                    <Textarea
                      value={formData.additional_notes}
                      onChange={(e) => setFormData({...formData, additional_notes: e.target.value})}
                      placeholder="Any additional information relevant to resident's stay..."
                      rows={4}
                    />
                  </div>
                </TabsContent>

                {/* PATH Program Tab */}
                <TabsContent value="path" className="space-y-4">
                  <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-3 text-sm text-indigo-800">
                    PATH (Projects for Assistance in Transition from Homelessness) data elements. Complete these for clients enrolled in the SAMHSA PATH program — they feed the IL PATH export in Reports.
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Enrolled in PATH?</label>
                      <Select value={formData.path_enrolled} onValueChange={(value) => setFormData({...formData, path_enrolled: value})}>
                        <SelectTrigger data-testid="path-enrolled"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">PATH Status</label>
                      <Select value={formData.path_status} onValueChange={(value) => setFormData({...formData, path_status: value})}>
                        <SelectTrigger data-testid="path-status"><SelectValue placeholder="Select status" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="enrolled_active">Enrolled - Active</SelectItem>
                          <SelectItem value="enrolled_inactive">Enrolled - Inactive</SelectItem>
                          <SelectItem value="not_enrolled">Not Enrolled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Date of Engagement</label>
                      <Input type="date" value={formData.path_date_of_engagement} onChange={(e) => setFormData({...formData, path_date_of_engagement: e.target.value})} data-testid="path-engagement-date" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Date of Status Determination</label>
                      <Input type="date" value={formData.path_date_of_status_determination} onChange={(e) => setFormData({...formData, path_date_of_status_determination: e.target.value})} data-testid="path-status-date" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Connection with SOAR</label>
                      <Select value={formData.path_connection_soar} onValueChange={(value) => setFormData({...formData, path_connection_soar: value})}>
                        <SelectTrigger data-testid="path-soar"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="declined">Client Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Current Living Situation</label>
                      <Select value={formData.path_current_living_situation} onValueChange={(value) => setFormData({...formData, path_current_living_situation: value})}>
                        <SelectTrigger data-testid="path-living-situation"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="emergency_shelter">Emergency Shelter</SelectItem>
                          <SelectItem value="transitional_housing">Transitional Housing</SelectItem>
                          <SelectItem value="street">Street / Place not meant for habitation</SelectItem>
                          <SelectItem value="vehicle">Vehicle</SelectItem>
                          <SelectItem value="abandoned_building">Abandoned Building</SelectItem>
                          <SelectItem value="friend_family_couch">Staying with Friend/Family</SelectItem>
                          <SelectItem value="hotel_motel">Hotel / Motel</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">PATH-Funded Services Provided</label>
                    <Textarea value={formData.path_services_provided} onChange={(e) => setFormData({...formData, path_services_provided: e.target.value})} placeholder="e.g., Outreach, Case Management, Housing Services..." rows={2} data-testid="path-services" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">PATH Referrals Provided</label>
                    <Textarea value={formData.path_referrals_provided} onChange={(e) => setFormData({...formData, path_referrals_provided: e.target.value})} placeholder="e.g., Community Mental Health, Primary Health, SSI/SSDI..." rows={2} data-testid="path-referrals" />
                  </div>
                </TabsContent>
                  </Tabs>
                  
                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                      {editingResident ? 'Update Resident' : 'Complete Intake'}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Status Tabs and Search */}
      <Card className="card-shadow">
        <CardContent className="p-6">
          <Tabs value={selectedStatus} onValueChange={setSelectedStatus}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
              <TabsList className="grid w-full sm:w-auto grid-cols-3">
                <TabsTrigger value="current" className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4" />
                  <span>Current</span>
                </TabsTrigger>
                <TabsTrigger value="past" className="flex items-center space-x-2">
                  <Clock className="h-4 w-4" />
                  <span>Past</span>
                </TabsTrigger>
                <TabsTrigger value="banned" className="flex items-center space-x-2">
                  <XCircle className="h-4 w-4" />
                  <span>Banned</span>
                </TabsTrigger>
              </TabsList>
              
              <div className="mt-4 sm:mt-0 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search residents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full sm:w-64"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-2 mb-6">
              <StatusIcon className={`h-5 w-5 ${currentConfig.color}`} />
              <div>
                <h3 className="font-semibold text-gray-900">{currentConfig.title}</h3>
                <p className="text-sm text-gray-600">{currentConfig.description}</p>
              </div>
            </div>
          </Tabs>
        </CardContent>
      </Card>

      {/* Residents List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="card-shadow">
              <CardContent className="p-6">
                <div className="skeleton h-32 w-full rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredResidents.length === 0 ? (
        <Card className="card-shadow">
          <CardContent className="p-12 text-center">
            <Users className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchTerm ? 'No residents found' : `No ${selectedStatus} residents`}
            </h3>
            <p className="text-gray-600 mb-4">
              {searchTerm 
                ? 'Try adjusting your search terms'
                : `There are currently no residents in the ${selectedStatus} status.`
              }
            </p>
            {selectedStatus === 'current' && !searchTerm && (
              <Button onClick={() => setShowAddDialog(true)} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="h-4 w-4 mr-2" />
                Add First Resident
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {orderedResidents.map((resident, idx) => {
            const prev = orderedResidents[idx - 1];
            const isFirstOfHousehold = resident.household_id && resident.household_id !== prev?.household_id;
            const inHousehold = !!resident.household_id;
            return (
            <React.Fragment key={resident.id}>
              {isFirstOfHousehold && (
                <div className="col-span-full flex items-center gap-2 mt-2 -mb-2" data-testid={`household-divider-${resident.household_id}`}>
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-3 py-1">
                    <Users className="h-4 w-4" /> {resident.last_name} Household · {householdSizes[resident.household_id]} member{householdSizes[resident.household_id] > 1 ? 's' : ''}
                  </div>
                  <div className="flex-1 h-px bg-indigo-100" />
                </div>
              )}
              <Card className={`card-shadow hover:card-shadow-lg transition-shadow ${inHousehold ? 'border-l-4 border-l-indigo-400' : ''}`}>
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">
                      {resident.first_name} {resident.last_name}
                    </CardTitle>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      {(() => {
                        const info = getAgeInfo(resident.date_of_birth);
                        if (!info) return null;
                        return (
                          <span
                            className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${info.isMinor ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}`}
                            title={info.isMinor ? 'Minor (under 18)' : 'Adult (18+)'}
                            data-testid={`resident-age-badge-${resident.id}`}
                          >
                            {info.isMinor ? 'Minor' : 'Adult'} · {info.age} yrs
                          </span>
                        );
                      })()}
                      {resident.household_id && (
                        <span
                          className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800"
                          title="Part of a family household"
                          data-testid={`resident-household-badge-${resident.id}`}
                        >
                          <Users className="h-3 w-3" />
                          {resident.relationship_to_head === 'head' ? 'Household · Head'
                            : resident.relationship_to_head === 'spouse' ? 'Household · Spouse'
                            : resident.relationship_to_head === 'child' ? 'Household · Child'
                            : 'Household'}
                        </span>
                      )}
                    </div>
                    {selectedStatus === 'current' && (
                      <div className="flex items-center space-x-2 mt-2">
                        <div className={`inline-block stay-indicator ${getStayColor(resident.stay_length_days)}`}>
                          {getStayLabel(resident.stay_length_days)}
                        </div>
                        {(() => {
                          const signed = consentCounts[resident.id] || 0;
                          const complete = signed >= 3;
                          return (
                            <span
                              className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${complete ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}
                              title={complete ? 'All required consents signed' : 'Required consents incomplete'}
                              data-testid={`resident-consent-badge-${resident.id}`}
                            >
                              {complete ? <CheckCircle className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                              Consents {signed}/3
                            </span>
                          );
                        })()}
                      </div>
                    )}
                    {/* Show dates for past and banned residents */}
                    {selectedStatus === 'past' && (
                      <div className="text-sm text-gray-600 mt-1 space-y-1">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-3 w-3" />
                          <span>Departed: {formatDate(resident.departure_date)}</span>
                        </div>
                        {resident.departure_reason && (
                          <div className="flex items-center space-x-2">
                            <FileText className="h-3 w-3" />
                            <span>Reason: {resident.departure_reason.replace('_', ' ')}</span>
                          </div>
                        )}
                        {!isEligibleForReadmission(resident) && resident.readmission_date && (
                          <div className="flex items-center space-x-2 text-orange-600">
                            <Clock className="h-3 w-3" />
                            <span>Eligible: {formatDate(resident.readmission_date)}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {selectedStatus === 'banned' && (
                      <div className="text-sm text-red-600 mt-1 space-y-1">
                        <div className="flex items-center space-x-2">
                          <XCircle className="h-3 w-3" />
                          <span>Banned: {formatDate(resident.ban_date)}</span>
                        </div>
                        {resident.ban_staff_name && (
                          <div className="flex items-center space-x-2">
                            <Users className="h-3 w-3" />
                            <span>By: {resident.ban_staff_name}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setConsentResident(resident); setShowConsentCapture(true); }}
                      className="text-gray-600 hover:text-emerald-600"
                      title="Signatures & consent forms"
                      data-testid={`resident-consent-btn-${resident.id}`}
                    >
                      <PenTool className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEdit(resident)}
                      className="text-gray-600 hover:text-indigo-600"
                      title={isGeneralStaff ? 'Request a change (case worker will apply)' : 'Edit resident'}
                      data-testid={`resident-edit-btn-${resident.id}`}
                    >
                      {isGeneralStaff ? <MessageSquarePlus className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-3">
                {resident.date_of_birth && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Calendar className="h-4 w-4" />
                    <span>Born: {new Date(resident.date_of_birth).toLocaleDateString()}</span>
                  </div>
                )}
                
                {resident.phone && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Phone className="h-4 w-4" />
                    <span>{resident.phone}</span>
                  </div>
                )}
                
                {selectedStatus === 'current' && resident.room_number && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <MapPin className="h-4 w-4" />
                    <span>Room {resident.room_number}</span>
                  </div>
                )}
                
                {selectedStatus === 'current' && !resident.room_number && (
                  <div className="flex items-center space-x-2 text-sm text-orange-600">
                    <MapPin className="h-4 w-4" />
                    <span>No room assigned</span>
                  </div>
                )}

                {selectedStatus === 'current' && !resident.room_number && compatibleRooms(resident).length > 0 && (
                  <div className="pl-6 space-y-1" data-testid={`suggested-rooms-${resident.id}`}>
                    <p className="text-xs font-medium text-gray-500">Suggested open beds:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {compatibleRooms(resident).slice(0, 4).map((r) => (
                        <Button
                          key={r.id}
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                          onClick={() => assignToRoom(r.id, resident.id)}
                          title={`Assign to Room ${r.room_number}`}
                          data-testid={`assign-suggest-${resident.id}-${r.id}`}
                        >
                          <UserPlus className="h-3 w-3 mr-1" />
                          {r.room_number} · {r.capacity - r.current_occupancy} free
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                {resident.emergency_contact && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <AlertTriangle className="h-4 w-4" />
                    <span>Emergency: {resident.emergency_contact}</span>
                  </div>
                )}
                
                {resident.medical_notes && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-red-900">Medical Notes</p>
                        <p className="text-sm text-red-700">{resident.medical_notes}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {resident.special_needs && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <FileText className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-blue-900">Special Needs</p>
                        <p className="text-sm text-blue-700">{resident.special_needs}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Print Actions */}
                <div className="pt-4 border-t space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePrintResidentProfile(resident)}
                      className="text-blue-600 hover:text-blue-700 border-blue-200 hover:border-blue-300"
                    >
                      <Printer className="h-4 w-4 mr-1" />
                      Profile
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePrintCombinedReport(resident)}
                      className="text-green-600 hover:text-green-700 border-green-200 hover:border-green-300"
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Complete Report
                    </Button>
                  </div>
                  
                  {/* Status Change Actions */}
                  {selectedStatus === 'current' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedResidentForAction(resident);
                          setShowDepartDialog(true);
                        }}
                        className="w-full text-orange-600 hover:text-orange-700 border-orange-200 hover:border-orange-300"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Process Departure
                      </Button>
                      <div className="w-full">
                        <EvictionForm 
                          resident={resident} 
                          onEvictionComplete={fetchResidents} 
                        />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedResidentForAction(resident);
                          setShowBanDialog(true);
                        }}
                        className="w-full text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                      >
                        <UserX className="h-4 w-4 mr-2" />
                        Ban Resident
                      </Button>
                    </>
                  )}
                  
                  {selectedStatus === 'past' && (
                    <>
                      {isEligibleForReadmission(resident) ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleReadmitResident(resident.id)}
                          className="w-full text-green-600 hover:text-green-700 border-green-200 hover:border-green-300"
                        >
                          <UserCheck className="h-4 w-4 mr-2" />
                          Readmit Resident
                        </Button>
                      ) : (
                        <div className="space-y-2">
                          <div className="p-3 bg-orange-50 border border-orange-200 rounded text-center">
                            <Clock className="h-4 w-4 mx-auto text-orange-600 mb-1" />
                            <p className="text-xs text-orange-700">
                              Eligible for readmission on<br />
                              {formatDate(resident.readmission_date)}
                            </p>
                          </div>
                          {user?.role === 'admin' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAdminOverrideReadmit(resident.id, `${resident.first_name} ${resident.last_name}`)}
                              className="w-full text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                            >
                              <Gavel className="h-4 w-4 mr-2" />
                              Admin Override Readmit
                            </Button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  
                  {selectedStatus === 'banned' && (
                    <div className="space-y-2">
                      <div className="p-3 bg-red-50 border border-red-200 rounded text-center">
                        <AlertTriangle className="h-4 w-4 mx-auto text-red-600 mb-1" />
                        <p className="text-xs text-red-700">
                          Permanently banned<br />
                          Admin approval required for readmission
                        </p>
                        {resident.ban_reason && (
                          <p className="text-xs text-red-600 mt-2 font-medium">
                            Reason: {resident.ban_reason.length > 50 
                              ? resident.ban_reason.substring(0, 50) + '...' 
                              : resident.ban_reason}
                          </p>
                        )}
                      </div>
                      {user?.role === 'admin' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAdminOverrideReadmit(resident.id, `${resident.first_name} ${resident.last_name}`)}
                          className="w-full text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                        >
                          <Gavel className="h-4 w-4 mr-2" />
                          Admin Override Readmit
                        </Button>
                      )}
                    </div>
                  )}
                  
                  {/* Print Action - Available to all users */}
                  <div className="pt-2 border-t space-y-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setFilesResident(resident); setShowFilesDialog(true); }}
                      className="w-full text-indigo-600 hover:text-indigo-700 border-indigo-200 hover:border-indigo-300"
                      data-testid={`files-btn-${resident.id}`}
                    >
                      <ImageIcon className="h-4 w-4 mr-2" />
                      Files & Photos
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => printResidentData(resident)}
                      className="w-full text-blue-600 hover:text-blue-700 border-blue-200 hover:border-blue-300"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Print Resident Data
                    </Button>
                  </div>
                  
                  {/* Admin Actions */}
                  {user?.role === 'admin' && (
                    <div className="pt-2 border-t space-y-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => startEdit(resident)}
                        className="w-full"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Resident
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setResidentToDelete(resident);
                          setShowDeleteResidentDialog(true);
                        }}
                        className="w-full text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Resident
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Enhanced Departure Dialog */}
      <DepartureDialog
        open={showDepartDialog}
        onOpenChange={setShowDepartDialog}
        resident={selectedResidentForAction}
        departData={departData}
        setDepartData={setDepartData}
        onConfirm={handleDepartResident}
        onCancel={resetDepartDialog}
      />

      {/* Enhanced Ban Dialog */}
      <BanDialog
        open={showBanDialog}
        onOpenChange={setShowBanDialog}
        resident={selectedResidentForAction}
        banData={banData}
        setBanData={setBanData}
        onConfirm={handleBanResident}
        onCancel={resetBanDialog}
      />

      {/* Printable Forms */}
      <PrintableForm
        open={showPrintDialog}
        onOpenChange={setShowPrintDialog}
        printData={printData}
      />

      {/* Delete Staff Confirmation Dialog */}
      <Dialog open={showDeleteStaffDialog} onOpenChange={setShowDeleteStaffDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Staff Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove {staffToDelete?.full_name} from the system? 
              This action cannot be undone and they will lose access to Shelter Care Connect.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowDeleteStaffDialog(false);
                setStaffToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteStaff}
            >
              Delete Staff Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Resident Confirmation Dialog */}
      <Dialog open={showDeleteResidentDialog} onOpenChange={setShowDeleteResidentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Resident</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete {residentToDelete?.first_name} {residentToDelete?.last_name} from the system? 
              <br /><br />
              <strong className="text-red-600">WARNING: This action cannot be undone.</strong>
              <br />
              • All resident data will be permanently removed
              • Room assignments will be updated
              • Case notes may be affected
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowDeleteResidentDialog(false);
                setResidentToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteResident}
            >
              Permanently Delete Resident
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resident Document Print Dialog */}
      {printResident && (
        <ResidentDocumentPrint
          isOpen={showResidentPrintDialog}
          onClose={() => setShowResidentPrintDialog(false)}
          resident={printResident}
          caseNotes={printResident.caseNotes || []}
          incidentReports={printResident.incidentReports || []}
          consents={printResident.consents || []}
          printType={printType}
        />
      )}

      {/* After-hours Change Request Dialog (general staff) */}
      <Dialog open={showChangeRequestDialog} onOpenChange={setShowChangeRequestDialog}>
        <DialogContent className="max-w-lg" data-testid="change-request-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquarePlus className="h-5 w-5 text-amber-600" />
              Request Resident Change
            </DialogTitle>
            <DialogDescription>
              {changeRequestResident && (
                <span className="font-medium text-gray-700">
                  {changeRequestResident.first_name} {changeRequestResident.last_name}
                </span>
              )}
              {' '}— As general staff you cannot edit records directly. Describe the change and a case worker will apply it at the next shift.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={changeRequestText}
              onChange={(e) => setChangeRequestText(e.target.value)}
              placeholder="e.g., Update emergency contact phone to 555-1234; resident reports new medication allergy..."
              rows={5}
              data-testid="change-request-textarea"
            />
          </div>
          <div className="flex justify-end space-x-3 pt-2">
            <Button variant="outline" onClick={() => setShowChangeRequestDialog(false)} data-testid="change-request-cancel-btn">
              Cancel
            </Button>
            <Button onClick={submitChangeRequest} data-testid="change-request-submit-btn">
              Submit Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConsentCaptureDialog
        resident={consentResident}
        open={showConsentCapture}
        enforceRequired={consentEnforce}
        onClose={() => { setShowConsentCapture(false); setConsentEnforce(false); fetchConsentCounts(); }}
      />

      <ResidentFiles
        residentId={filesResident?.id}
        residentName={filesResident ? `${filesResident.first_name} ${filesResident.last_name}` : ''}
        open={showFilesDialog}
        onOpenChange={setShowFilesDialog}
      />
    </div>
  );
};

export default ResidentManagement;